'use client';

import React from 'react';
import { ImagesSlider } from '@/components/ui/image-slider';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { Button } from './ui/moving-border';
import { ColourfulText } from './ui/colourful-text';
import Link from 'next/link';
import type { HeroContent, LocaleCode } from '@/types/sections';
import { pickLocalizedString } from '@/lib/locale';
import { getLocalizedHref } from '@/lib/site';
import { useIsMobile } from '@/hooks/use-mobile';
import { sanitizeImageUrl, fallbackImage } from '@/lib/image-safety';

const SparklesCore = dynamic(() => import('./ui/sparkles').then((m) => m.SparklesCore), {
  ssr: false,
  loading: () => null,
});

type Props = {
  hero: HeroContent;
  locale: LocaleCode;
};

export function HeroSlider({ hero, locale }: Props) {
  const images = (hero.slides ?? [])
    .map((slide) => sanitizeImageUrl(slide.imageUrl))
    .filter(Boolean);
  const heroImages = images.length > 0 ? images : [fallbackImage()];
  const headline = pickLocalizedString(hero.headline, locale);
  const highlight = pickLocalizedString(hero.highlight, locale);
  const ctaLabel = pickLocalizedString(hero.ctaLabel, locale, 'Join The Club →');
  const ctaHref = getLocalizedHref(locale, hero.ctaHref || '/#join');
  const isMobile = useIsMobile();

  return (
    <ImagesSlider className='h-[100vh] w-full max-w-full' images={heroImages}>
      <div className='z-50 flex flex-col justify-center items-center'>
        <Image
          src='/logo-dark-rounded.svg'
          loading='eager'
          fetchPriority='high'
          id='hero-logo'
          alt='Uncommon 12 logo'
          width={150}
          height={150}
        />

        {!isMobile && (
          <div className='w-[15rem] h-8 relative'>
            <SparklesCore
              background='transparent'
              minSize={0.4}
              maxSize={1.4}
              particleDensity={220}
              className='w-full h-full'
            />
          </div>
        )}
        <h2 className='text-2xl md:text-4xl font-semibold mb-4 text-white'>
          {headline}{' '}
          <span className='rounded-xl p-1'>
            <ColourfulText text={highlight} />
          </span>
        </h2>

        <Link href={ctaHref}>
          <Button>{ctaLabel}</Button>
        </Link>
      </div>
    </ImagesSlider>
  );
}
